import { motion } from 'framer-motion'
import { Download, CheckCircle, AlertTriangle, Monitor, HardDrive, Wifi } from 'lucide-react'

const InstallGuide = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="prose prose-invert max-w-none"
    >
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <Download className="mr-3 text-accent-cyan" size={32} />
        安装指南
      </h1>

      {/* 系统要求 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">系统要求</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 not-prose">
          <div className="glass-effect rounded-lg p-4">
            <Monitor className="text-accent-cyan mb-2" size={24} />
            <h3 className="font-semibold mb-2">操作系统</h3>
            <p className="text-sm text-gray-400">Windows 10/11 (64位)</p>
          </div>
          <div className="glass-effect rounded-lg p-4">
            <HardDrive className="text-accent-cyan mb-2" size={24} />
            <h3 className="font-semibold mb-2">存储空间</h3>
            <p className="text-sm text-gray-400">至少 500MB 可用空间</p>
          </div>
          <div className="glass-effect rounded-lg p-4">
            <Wifi className="text-accent-cyan mb-2" size={24} />
            <h3 className="font-semibold mb-2">网络连接</h3>
            <p className="text-sm text-gray-400">需要联网使用AI服务</p>
          </div>
        </div>
      </section>

      {/* 下载安装 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">下载与安装</h2>
        
        <div className="space-y-4">
          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              1
            </div>
            <div>
              <h3 className="font-semibold mb-2">下载安装包</h3>
              <p className="text-gray-400 mb-2">
                访问 <a href="/download" className="text-accent-cyan hover:underline">下载页面</a>，点击"立即下载"按钮获取最新版本。
              </p>
              <ul className="text-sm text-gray-400 space-y-1 ml-4">
                <li>• 文件大小约 100-300MB</li>
                <li>• 下载完成后请验证 MD5 值</li>
              </ul>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              2
            </div>
            <div>
              <h3 className="font-semibold mb-2">运行安装程序</h3>
              <p className="text-gray-400 mb-2">
                双击下载的 <code className="px-2 py-1 bg-black/30 rounded">Avelia-Setup.exe</code> 文件。
              </p>
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start space-x-2 mt-2">
                <AlertTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" size={18} />
                <p className="text-sm text-yellow-200">
                  Windows 可能会显示安全警告，点击"更多信息"然后选择"仍要运行"。
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              3
            </div>
            <div>
              <h3 className="font-semibold mb-2">完成安装</h3>
              <p className="text-gray-400 mb-2">
                按照安装向导的提示完成安装：
              </p>
              <ul className="text-sm text-gray-400 space-y-1 ml-4">
                <li>• 选择安装路径（建议使用默认路径）</li>
                <li>• 选择是否创建桌面快捷方式</li>
                <li>• 等待安装完成（通常需要 1-2 分钟）</li>
              </ul>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 glass-effect rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-accent-purple to-accent-cyan flex items-center justify-center font-bold">
              4
            </div>
            <div>
              <h3 className="font-semibold mb-2">首次启动</h3>
              <p className="text-gray-400 mb-2">
                安装完成后，启动 Avelia：
              </p>
              <ul className="text-sm text-gray-400 space-y-1 ml-4">
                <li>• 双击桌面图标，或从开始菜单启动</li>
                <li>• 首次启动会进行初始化配置</li>
                <li>• 根据引导完成基础设置</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 常见问题 */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-accent-purple">安装常见问题</h2>
        
        <div className="space-y-4">
          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between">
              安装时提示"Windows 已保护你的电脑"
              <CheckCircle className="text-accent-cyan group-open:rotate-90 transition-transform" size={18} />
            </summary>
            <div className="mt-3 text-sm text-gray-400 pl-4 border-l-2 border-accent-cyan/30">
              <p className="mb-2">这是 Windows SmartScreen 的正常保护机制。解决方法：</p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>点击"更多信息"链接</li>
                <li>点击"仍要运行"按钮</li>
                <li>如果仍有疑问，可以验证安装包的数字签名</li>
              </ol>
            </div>
          </details>

          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between">
              安装后无法启动程序
              <CheckCircle className="text-accent-cyan group-open:rotate-90 transition-transform" size={18} />
            </summary>
            <div className="mt-3 text-sm text-gray-400 pl-4 border-l-2 border-accent-cyan/30">
              <p className="mb-2">可能的原因和解决方法：</p>
              <ul className="list-disc ml-4 space-y-1">
                <li>检查是否安装了最新的 Windows 更新</li>
                <li>确认系统满足最低要求（Windows 10/11 64位）</li>
                <li>尝试以管理员身份运行</li>
                <li>检查杀毒软件是否拦截了程序</li>
              </ul>
            </div>
          </details>

          <details className="glass-effect rounded-lg p-4 cursor-pointer group">
            <summary className="font-semibold flex items-center justify-between">
              如何卸载 Avelia
              <CheckCircle className="text-accent-cyan group-open:rotate-90 transition-transform" size={18} />
            </summary>
            <div className="mt-3 text-sm text-gray-400 pl-4 border-l-2 border-accent-cyan/30">
              <p className="mb-2">两种卸载方式：</p>
              <ol className="list-decimal ml-4 space-y-2">
                <li>
                  <strong>通过控制面板：</strong>
                  <br />设置 → 应用 → 已安装的应用 → 找到 Avelia → 卸载
                </li>
                <li>
                  <strong>通过安装目录：</strong>
                  <br />在安装目录中运行 <code className="px-2 py-1 bg-black/30 rounded">uninstall.exe</code>
                </li>
              </ol>
            </div>
          </details>
        </div>
      </section>

      {/* 下一步 */}
      <section className="mt-8 p-6 glass-effect rounded-lg border border-accent-cyan/30">
        <h3 className="text-xl font-semibold mb-3 flex items-center">
          <CheckCircle className="mr-2 text-green-400" size={24} />
          安装完成！
        </h3>
        <p className="text-gray-400 mb-4">
          恭喜！你已成功安装 Avelia。接下来：
        </p>
        <ul className="space-y-2 text-sm text-gray-400">
          <li className="flex items-center">
            <span className="w-2 h-2 rounded-full bg-accent-cyan mr-2"></span>
            查看 <strong className="text-white mx-1">基本使用</strong> 了解如何开始使用
          </li>
          <li className="flex items-center">
            <span className="w-2 h-2 rounded-full bg-accent-cyan mr-2"></span>
            阅读 <strong className="text-white mx-1">配置模型</strong> 添加你的 AI 模型
          </li>
          <li className="flex items-center">
            <span className="w-2 h-2 rounded-full bg-accent-cyan mr-2"></span>
            探索 <strong className="text-white mx-1">功能教程</strong> 发现更多能力
          </li>
        </ul>
      </section>
    </motion.div>
  )
}

export default InstallGuide
